import { CatalogueDataType } from 'src/lib/types/common/catalogue'

export enum CustomerEligibilityV2Type {
  ALL_CUSTOMERS = 'ALL_CUSTOMERS',
  FIRST_TIME_CUSTOMERS = 'FIRST_TIME_CUSTOMERS',
  LIFETIME_ORDER_COUNT = 'LIFETIME_ORDER_COUNT',
  SPECIFIC_CUSTOMERS = 'SPECIFIC_CUSTOMERS',
  CUSTOMER_SPECIFIC = 'CUSTOMER_SPECIFIC',
  UTM_PARAMS = 'UTM_PARAMS'
}

export enum DiscountStackMode {
  ALL = 'ALL',
  NONE = 'NONE',
  CUSTOM = 'CUSTOM'
}

export type CustomerEligibilityFormV2Type =
  | {
      type: CustomerEligibilityV2Type.ALL_CUSTOMERS
    }
  | {
      type: CustomerEligibilityV2Type.FIRST_TIME_CUSTOMERS
    }
  | {
      type: CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT
      prerequisiteUserOrderCohort: {
        lifetimeMaxOrderCount: number | null
        lifetimeMinOrderCount: number
      }
    }
  | {
      type: CustomerEligibilityV2Type.CUSTOMER_SPECIFIC
      segmentIds: string[]
      overridePreEvaluatedSegment: boolean
    }
  | {
      type: CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS
      customerSpecific: boolean
      fileMeta: {
        fileUrl: string
        fileName?: string
        size?: number
        downloadUrl?: string
        fileKey?: string
      }
    }
  | {
      type: CustomerEligibilityV2Type.UTM_PARAMS
      utmParams: {
        source: string[]
        medium: string[]
        campaign: string[]
        content: string[]
      }
    }

export interface UsageLimitFormV2Type {
  maxApplicableCount?: number
  maxApplicablePerUserCount?: number
}

export type DiscountStackabilityFormV2Type =
  | {
      stackMode: DiscountStackMode.ALL
    }
  | {
      stackMode: DiscountStackMode.NONE
    }
  | {
      stackMode: DiscountStackMode.CUSTOM
      customStackableDiscountIds: string[]
    }

export interface SalesChannelV2 {
  id: string
  name: string
  description: string | null
}

export interface AvailabilityFormV2Type {
  enabledChannelIds: string[]
  startDate: number | null
  endDate: number | null
  discoverable: boolean
  active: boolean
}

export enum DiscountMethod {
  CODE = 'CODE',
  AUTOMATIC = 'AUTOMATIC'
}

export interface DiscountApplicationMethodFormV2Type {
  method: DiscountMethod
  autoApplicable: boolean
  discountCode: string
  title: string
}

export interface DiscountPreviewV2Type {
  header: string
  description: string
  longDescription: string
}

export enum CouponType {
  AMOUNT = 'AMOUNT',
  BUNDLE = 'BUNDLE',
  BXGY = 'BXGY',
  SHIPPING = 'SHIPPING',
  EXIT_COUPON = 'EXIT_COUPON'
}

export enum AmountType {
  PERCENTAGE = 'PERCENTAGE',
  FLAT = 'FLAT'
}

export enum bxgyProductStrategy {
  ADD_PRODUCT = 'ADD_PRODUCT',
  DISCOUNT_ON_PRODUCT = 'DISCOUNT_ON_PRODUCT'
}

export enum generationSourceType {
  SINGLE = 'SINGLE',
  BULK = 'BULK'
}

export enum UiConfigDiscountType {
  BUNDLE = 'BUNDLE',
  AMOUNT_OFF = 'AMOUNT_OFF',
  AMOUNT_OFF_UPTO = 'AMOUNT_OFF_UPTO',
  AMOUNT_OFF_PRODUCTS = 'AMOUNT_OFF_PRODUCTS',
  BXGY = 'BXGY',
  BXGY_AUTO_ADDED = 'BXGY_AUTO_ADDED',
  SHIPPING = 'SHIPPING',
  FREEBIE = 'FREEBIE',
  FREEBIE_SELECTOR = 'FREEBIE_SELECTOR',
  EXIT_COUPON = 'EXIT_COUPON',
}

export type BundleUiConfig = {
  type: UiConfigDiscountType.BUNDLE
  bundle: {
    applyTo: DiscountApplyToType
  }
}

// Simplified UiConfig types
export interface UiConfigBase {
  type: UiConfigDiscountType
}

export type uiConfigType = UiConfigBase

export enum PurchaseConditionType {
  NONE = 'NONE',
  CART_AMOUNT = 'CART_AMOUNT',
  CART_QUANTITY = 'CART_QUANTITY'
}

export enum PurchaseConditionAmountType {
  ENTIRE_CART = 'ENTIRE_CART',
  SPECIFIC_PRODUCTS = 'SPECIFIC_PRODUCTS',
  SPECIFIC_COLLECTIONS = 'SPECIFIC_COLLECTIONS'
}

export enum DiscountApplyToType {
  ALL_PRODUCTS = 'ALL_PRODUCTS',
  SPECIFIC_PRODUCTS = 'SPECIFIC_PRODUCTS',
  SPECIFIC_COLLECTIONS = 'SPECIFIC_COLLECTIONS'
}

export enum EntitledCatalogueEntityType {
  PRODUCT = 'PRODUCT',
  COLLECTION = 'COLLECTION',
  VARIANT = 'VARIANT',
  PRODUCT_AND_VARIANT = 'PRODUCT_AND_VARIANT',
  EXCLUDE = 'EXCLUDE'
}
export interface EntitledCatalogueEntity {
  type: EntitledCatalogueEntityType
  entityId: string[]
}

export enum EntitledCatalogueSelectionMode {
  ALL = 'ALL',
  ANY = 'ANY'
}

export interface EntitledCatalogueType {
  quantity?: number | null
  entities: EntitledCatalogueEntity[]
  selectionMode: EntitledCatalogueSelectionMode
}

export type ExcludedCatalogueType = {
  entities: ExcludedCatalogueEntity[]
}

export interface ExcludedCatalogueEntity {
  type: EntitledCatalogueEntityType
  entityId: string[]
}

export enum PrerequisiteType {
  PRICE = 'PRICE',
  QUANTITY = 'QUANTITY',
  NONE = 'NONE'
}

export interface PrerequisiteCatalogueQuantityType {
  type: PrerequisiteType.QUANTITY
  quantity: number
  maxQuantity?: number
  entities: PrerequisiteCatalogueEntity[]
}

export interface PrerequisiteCataloguePriceType {
  type: PrerequisiteType.PRICE
  price: number
  maxPrice?: number
  entities: PrerequisiteCatalogueEntity[]
}

export type PrerequisiteCatalogueType = PrerequisiteCatalogueQuantityType | PrerequisiteCataloguePriceType

export interface PrerequisiteCatalogueEntity {
  type: PrerequisiteCatalogueEntityType
  entityId: string[]
}

export enum PrerequisiteCatalogueEntityType {
  PRODUCT = 'PRODUCT',
  COLLECTION = 'COLLECTION',
  VARIANT = 'VARIANT',
  EXCLUDE = 'EXCLUDE'
}

export interface DiscountMetaData {
  coveredCatalogue: CatalogueDataType[][]
  prerequisiteCatalogue: CatalogueDataType[][]
  entitledCatalogue: CatalogueDataType[][]
  excludedCatalogue: CatalogueDataType[][]
  prerequisiteExcludedCatalogue?: CatalogueDataType[][]
}

export enum ApplicationTarget {
  ALL = 'ALL',
  ENTITLED = 'ENTITLED'
}

interface BasePrerequisite {
  customerEligibility: CustomerEligibilityFormV2Type
  usageLimit: UsageLimitFormV2Type
  stackability: DiscountStackabilityFormV2Type
  availability: AvailabilityFormV2Type
}

export interface BundlePrerequisite extends BasePrerequisite {
  type: CouponType.BUNDLE
}

interface StandardBasePrerequisite extends BasePrerequisite {
  type: CouponType.AMOUNT | CouponType.BXGY | CouponType.SHIPPING
}

export interface PricePrerequisite extends StandardBasePrerequisite {
  prerequisiteType: PrerequisiteType.PRICE
  purchaseConditionType: PurchaseConditionType.CART_AMOUNT
  purchaseConditionAmountType: PurchaseConditionAmountType
  prerequisiteSubtotalRange: number | null
  prerequisiteMaxOrderValue: number | null
  prerequisiteCatalogue: PrerequisiteCatalogueType[]
  excludedCatalogue?: ExcludedCatalogueType[]
  metaData: {
    prerequisiteCatalogue: CatalogueDataType[][]
    excludedCatalogue?: CatalogueDataType[][]
  }
}

export interface QuantityPrerequisite extends StandardBasePrerequisite {
  prerequisiteType: PrerequisiteType.QUANTITY
  purchaseConditionType: PurchaseConditionType.CART_QUANTITY
  purchaseConditionAmountType: PurchaseConditionAmountType
  prerequisiteCountToEntitlement: number | null
  prerequisiteMaxCountForEntitlement: number | null
  prerequisiteCatalogue: PrerequisiteCatalogueType[]
  excludedCatalogue?: ExcludedCatalogueType[]
  metaData: {
    prerequisiteCatalogue: CatalogueDataType[][]
    excludedCatalogue?: CatalogueDataType[][]
  }
}

export interface NonePrerequisite extends StandardBasePrerequisite {
  prerequisiteType: PrerequisiteType.NONE
  purchaseConditionType: PurchaseConditionType
  purchaseConditionAmountType: PurchaseConditionAmountType
  prerequisiteCatalogue: PrerequisiteCatalogueType[]
  prerequisiteCountToEntitlement: number | null
  excludedCatalogue?: ExcludedCatalogueType[]
  metaData: {
    prerequisiteCatalogue: CatalogueDataType[][]
    excludedCatalogue?: CatalogueDataType[][]
  }
}

export type StandardPrerequisite = PricePrerequisite | QuantityPrerequisite | NonePrerequisite

export type DiscountPrerequisite = BundlePrerequisite | StandardPrerequisite

interface BaseEntitlement {
  concessionAmount: number
  amountType: AmountType
  generationSource: generationSourceType
  applicationTarget: ApplicationTarget
  applicationMethod: DiscountApplicationMethodFormV2Type
}

export interface ShippingEntitlement extends BaseEntitlement {
  type: CouponType.SHIPPING
  maximumConcessionAmount?: number
}

export interface BundleEntitlement extends BaseEntitlement {
  type: CouponType.BUNDLE
  entitledCatalogue: EntitledCatalogueType[]
  excludedCatalogue: ExcludedCatalogueType[]
  entitledProductCount: number
  applyTo: DiscountApplyToType
  metaData: {
    excludedCatalogue: CatalogueDataType[][]
    entitledCatalogue: CatalogueDataType[][]
  }
  maxUsesPerOrder: number | null
}

export interface BxgyEntitlement extends BaseEntitlement {
  type: CouponType.BXGY
  bxgyProductStrategy: bxgyProductStrategy
  entitledCatalogue: EntitledCatalogueType[]
  excludedCatalogue: ExcludedCatalogueType[]
  entitledProductCount: number
  applyTo: DiscountApplyToType
  metaData: {
    excludedCatalogue: CatalogueDataType[][]
    entitledCatalogue: CatalogueDataType[][]
  }
  maxUsesPerOrder: number | null
}

export interface AmountEntitlement extends BaseEntitlement {
  type: CouponType.AMOUNT
  entitledCatalogue: EntitledCatalogueType[]
  excludedCatalogue: ExcludedCatalogueType[]
  maximumConcessionAmount?: number
  entitledProductCount: number
  applyTo: DiscountApplyToType
  metaData: {
    entitledCatalogue: CatalogueDataType[][]
    excludedCatalogue: CatalogueDataType[][]
  }
  maxUsesPerOrder: number | null
}

export type DiscountEntitlement = ShippingEntitlement | BundleEntitlement | BxgyEntitlement | AmountEntitlement

export interface DiscountConfiguration {
  preview: DiscountPreviewV2Type
  couponType: CouponType
}

export interface DiscountFormV2StateType {
  uiConfig: uiConfigType
  prerequisite: DiscountPrerequisite
  entitlement: DiscountEntitlement
  configuration: DiscountConfiguration
  errors: Record<string, string>
}

export interface DiscountBackendDataV2 {
  coupon_id: string
  code: string
  auto_applicable: boolean
  title: string
  coupon_type: CouponType
  amount_type: AmountType
  concession_amount: number
  maximum_concession_amount: number | null
  bxgy_product_strategy: bxgyProductStrategy
  generation_source: generationSourceType
  start_date: number | null
  end_date: number | null
  max_applicable_count?: number | null
  max_applicable_per_user_count?: number | null
  customer_specific: boolean
  segment_ids: string[]
  override_pre_evaluated_segment: boolean
  enabled_channels: string[]
  stack_config: {
    stack_type: string
    includes: string[]
  }
  multiple_application_options: {
    enable: boolean
    number_of_times_to_apply: number
  }
  prerequisite_user_order_cohort: {
    lifetime_min_order_count: number | null
    lifetime_max_order_count: number | null
  }
  utm_params: {
    source: string[] | null
    medium: string[] | null
    campaign: string[] | null
    content: string[] | null
  }
  file_meta: {
    download_url: string
    extension: string
    file_key: string
    file_name: string | null
    file_url: string | null
    headers: any | null
    human_readable_size: string
    mime_type: string
    size: number
  }
  header: string
  description: string
  long_description: string[]
  entitled_entity_quantity_distribution: Array<{
    quantity: number
    entities: Array<{
      type: EntitledCatalogueEntityType
      entity_id: string[]
    }>
  }>
  excluded_entity_ids: Array<{
    quantity: number
    entities: Array<{
      type: EntitledCatalogueEntityType
      entity_id: string[]
    }>
  }>
  prerequisite_excluded_entity_ids?: Array<{
    quantity: number
    entities: Array<{
      type: EntitledCatalogueEntityType
      entity_id: string[]
    }>
  }>
  prerequisite_entity_quantity_distribution: Array<{
    quantity?: number
    max_quantity?: number
    price?: number
    max_price?: number
    entities: Array<{
      type: PrerequisiteCatalogueEntityType
      entity_id: string[]
    }>
  }>
  entitled_product_count: number
  metadata: DiscountMetaData
  application_target: ApplicationTarget
  prerequisite_subtotal_range: number | null
  prerequisite_max_order_value: number | null
  prerequisite_count_to_entitlement: number | null
  prerequisite_max_count_for_entitlement: number | null
  discoverable: boolean
  active: boolean
}

export type DiscountResponseV2 = {
  success: boolean
  message: string
  data: DiscountBackendDataV2
}

export interface DiscountCreationMode {
  label: string
  value: string
  sidePanel: {
    title: string
    description: string
    examples: string[]
    importantNote: string
  }
  action: {
    type: string
    link: string
  }
  permissionId?: string
}

export const DiscountFieldPaths = {
  // Method
  METHOD_TITLE: 'entitlement.applicationMethod.title',
  METHOD_CODE: 'entitlement.applicationMethod.discountCode',

  // Entitlement
  ENTITLED_CATALOGUE: 'entitlement.entitledCatalogue',
  CONCESSION_AMOUNT: 'entitlement.concessionAmount',
  MAXIMUM_CONCESSION_AMOUNT: 'entitlement.maximumConcessionAmount',
  MAXUSES_PER_ORDER: 'entitlement.maxUsesPerOrder',

  // Prerequisite
  PREREQUISITE_CATALOGUE: 'prerequisite.prerequisiteCatalogue',
  PREREQUISITE_EXCLUDED_CATALOGUE: 'prerequisite.excludedCatalogue',
  PREREQUISITE_SUBTOTAL_RANGE: 'prerequisite.prerequisiteSubtotalRange',
  PREREQUISITE_MAX_ORDER_VALUE: 'prerequisite.prerequisiteMaxOrderValue',
  PREREQUISITE_COUNT_TO_ENTITLEMENT: 'prerequisite.prerequisiteCountToEntitlement',
  PREREQUISITE_MAX_COUNT_FOR_ENTITLEMENT: 'prerequisite.prerequisiteMaxCountForEntitlement',

  // Usage Limits
  MAX_APPLICABLE_COUNT: 'prerequisite.usageLimit.maxApplicableCount',
  MAX_APPLICABLE_PER_USER_COUNT: 'prerequisite.usageLimit.maxApplicablePerUserCount',

  // Availability
  START_DATE: 'prerequisite.availability.startDate',
  END_DATE: 'prerequisite.availability.endDate',
  ENABLED_CHANNEL_IDS: 'prerequisite.availability.enabledChannelIds'
} as const

export type DiscountFieldPath = typeof DiscountFieldPaths[keyof typeof DiscountFieldPaths]

export interface CouponSummary {
  coupon_id: string
  code: string
  title: string
  header?: string
  description?: string
  long_description?: string
}

export interface CouponSummaryResponse {
  message: string
  data: CouponSummary[]
}
